const WALLET_KEY = 'mech-league-credits';
const BETS_KEY = 'mech-league-bets';
const PAID_OUT_BETS_KEY = 'mech-league-paid-out-bets';

interface BetResult {
  totalCredits: number;
  betAmount: number;
}

interface PayoutResult {
  won: boolean;
  payout: number;
  newTotal: number;
  betKey?: string;
  betType?: string;
}

interface BetsByGame {
  [gameId: string]: {
    [teamId: string]: number;
  };
}

interface BetSearchResult {
  gameId: string;
  bets: { [teamId: string]: number };
}

interface PaidOutBets {
  [gameId: string]: {
    [betKey: string]: boolean;
  };
}

interface GamePlayerStats {
  name: string;
  mechClass: string;
  points: number;
  rebounds: number;
  assists: number;
  id?: string;
}

interface GameData {
  id: string;
  teams: {
    team1: { id: string; name: string };
    team2: { id: string; name: string };
  };
  score: {
    team1: number;
    team2: number;
  };
  playerStats: {
    team1: GamePlayerStats[];
    team2: GamePlayerStats[];
  };
}

export class Wallet {
  static getCredits(): number {
    const credits = localStorage.getItem(WALLET_KEY);
    return credits ? parseInt(credits, 10) : 3000; // Default starting credits
  }

  static setCredits(amount: number): void {
    localStorage.setItem(WALLET_KEY, amount.toString());
  }

  static addCredits(amount: number): number {
    const current = this.getCredits();
    this.setCredits(current + amount);
    return current + amount;
  }

  static deductCredits(amount: number): number {
    const current = this.getCredits();
    const newAmount = Math.max(0, current - amount);
    this.setCredits(newAmount);
    return newAmount;
  }

  static canAfford(amount: number): boolean {
    return this.getCredits() >= amount;
  }

  // Bets management
  static getBets(): BetsByGame {
    const bets = localStorage.getItem(BETS_KEY);
    return bets ? JSON.parse(bets) : {};
  }

  static setBets(bets: BetsByGame): void {
    localStorage.setItem(BETS_KEY, JSON.stringify(bets));
  }

  static placeBet(gameId: string, teamId: string, amount: number): BetResult {
    if (!this.canAfford(amount)) {
      throw new Error('Insufficient credits');
    }

    const bets = this.getBets();
    
    // Initialize game bets if not exists
    if (!bets[gameId]) {
      bets[gameId] = {};
    }
    
    // Initialize team bet if not exists
    if (!bets[gameId][teamId]) {
      bets[gameId][teamId] = 0;
    }
    
    // Add to existing bet
    bets[gameId][teamId] += amount;
    
    // Deduct credits
    const newTotal = this.deductCredits(amount);
    
    // Save bets
    this.setBets(bets);
    
    return {
      totalCredits: newTotal,
      betAmount: bets[gameId][teamId]
    };
  }

  static getAllBets(): BetsByGame {
    return this.getBets();
  }

  static getBetsForGame(gameId: string): { [teamId: string]: number } {
    const bets = this.getBets();
    return bets[gameId] || {};
  }

  // Get total amount bet on a specific game
  static getTotalBetForGame(gameId: string): number {
    const gameBets = this.getBetsForGame(gameId);
    return Object.values(gameBets).reduce((total, amount) => total + amount, 0);
  }

  static clearBetsForGame(gameId: string): void {
    const bets = this.getBets();
    delete bets[gameId];
    this.setBets(bets);
    
    // Also clear paid out bets tracking
    this.clearPaidOutBetsForGame(gameId);
  }

  static calculatePayout(gameId: string, winningTeamId: string, odds: number = 2.0): PayoutResult {
    // Check if already paid out
    if (this.isBetPaidOut(gameId, winningTeamId)) {
      return {
        won: false,
        payout: 0,
        newTotal: this.getCredits()
      };
    }
    
    const bets = this.getBetsForGame(gameId);
    const winningBet = bets[winningTeamId] || 0;
    
    let won = false;
    let payout = 0;
    
    if (winningBet > 0) {
      won = true;
      payout = Math.floor(winningBet * odds);
      
      // Add payout to wallet
      this.addCredits(payout);
      
      // Mark as paid out
      this.markBetAsPaidOut(gameId, winningTeamId);
    } else {
      // Mark as paid out even if lost (to prevent reprocessing)
      this.markBetAsPaidOut(gameId, winningTeamId);
    }
    
    // Don't clear bets - keep them visible for indicators even after payout
    // Bets are already marked as paid out, so they won't be processed again
    // this.clearBetsForGame(gameId);
    
    return {
      won,
      payout,
      newTotal: this.getCredits(),
      betKey: winningTeamId,
      betType: 'outcome'
    };
  }

  // Reset wallet (for testing)
  static reset(): void {
    localStorage.removeItem(WALLET_KEY);
    localStorage.removeItem(BETS_KEY);
    localStorage.removeItem(PAID_OUT_BETS_KEY);
  }

  // Migrate bets when next game becomes active game
  static migrateBet(nextGameId: string, activeGameId: string): void {
    const bets = this.getBets();
    if (bets[nextGameId]) {
      bets[activeGameId] = bets[nextGameId];
      delete bets[nextGameId];
      this.setBets(bets);
    }
    
    // Also migrate paid out bets tracking
    const paidOutBets = this.getPaidOutBets();
    if (paidOutBets[nextGameId]) {
      paidOutBets[activeGameId] = paidOutBets[nextGameId];
      delete paidOutBets[nextGameId];
      this.setPaidOutBets(paidOutBets);
    }
  }

  // Paid out bets tracking
  static getPaidOutBets(): PaidOutBets {
    const paidOut = localStorage.getItem(PAID_OUT_BETS_KEY);
    return paidOut ? JSON.parse(paidOut) : {};
  }

  static setPaidOutBets(paidOutBets: PaidOutBets): void {
    localStorage.setItem(PAID_OUT_BETS_KEY, JSON.stringify(paidOutBets));
  }

  static markBetAsPaidOut(gameId: string, betKey: string): void {
    const paidOutBets = this.getPaidOutBets();
    if (!paidOutBets[gameId]) {
      paidOutBets[gameId] = {};
    }
    paidOutBets[gameId][betKey] = true;
    this.setPaidOutBets(paidOutBets);
  }

  static isBetPaidOut(gameId: string, betKey: string): boolean {
    const paidOutBets = this.getPaidOutBets();
    return paidOutBets[gameId]?.[betKey] === true;
  }

  static clearPaidOutBetsForGame(gameId: string): void {
    const paidOutBets = this.getPaidOutBets();
    delete paidOutBets[gameId];
    this.setPaidOutBets(paidOutBets);
  }

  // Calculate over/under payout
  static calculateOverUnderPayout(
    gameId: string,
    betKey: string,
    actualValue: number,
    threshold: number,
    odds: number = 2.0
  ): PayoutResult {
    // Check if already paid out
    if (this.isBetPaidOut(gameId, betKey)) {
      return {
        won: false,
        payout: 0,
        newTotal: this.getCredits(),
        betKey,
        betType: 'over-under'
      };
    }

    const bets = this.getBetsForGame(gameId);
    const betAmount = bets[betKey] || 0;

    if (betAmount === 0) {
      return {
        won: false,
        payout: 0,
        newTotal: this.getCredits(),
        betKey,
        betType: 'over-under'
      };
    }

    // Parse bet key to determine over/under
    const isOver = betKey.includes('-over');
    const isUnder = betKey.includes('-under');

    let won = false;
    let payout = 0;

    if (isOver) {
      // For "over" bets, win if actual value is greater than threshold
      won = actualValue > threshold;
    } else if (isUnder) {
      // For "under" bets, win if actual value is less than threshold
      won = actualValue < threshold;
    } else {
      // Invalid bet key format
      this.markBetAsPaidOut(gameId, betKey);
      return {
        won: false,
        payout: 0,
        newTotal: this.getCredits(),
        betKey,
        betType: 'over-under'
      };
    }

    if (actualValue === threshold) {
      // Push - refund bet amount, no payout
      this.addCredits(betAmount);
      this.markBetAsPaidOut(gameId, betKey);
      return {
        won: false,
        payout: betAmount,
        newTotal: this.getCredits(),
        betKey,
        betType: 'over-under'
      };
    }

    if (won) {
      payout = Math.floor(betAmount * odds);
      this.addCredits(payout);
    }

    // Mark as paid out (whether won or lost)
    this.markBetAsPaidOut(gameId, betKey);

    return {
      won,
      payout,
      newTotal: this.getCredits(),
      betKey,
      betType: 'over-under'
    };
  }

  // Process all bets for a completed game (outcome and over/under)
  static processAllBetsForGame(
    gameId: string,
    gameData: GameData,
    team1AvgPoints: number,
    team2AvgPoints: number,
    selectedPlayers: {
      team1Points: { id: string; avgPoints: number } | null;
      team1Assists: { id: string; avgAssists: number } | null;
      team2Points: { id: string; avgPoints: number } | null;
      team2Assists: { id: string; avgAssists: number } | null;
    }
  ): PayoutResult[] {
    console.log(`💰 [WALLET] processAllBetsForGame called for gameId: ${gameId}`);
    console.log(`💰 [WALLET] Game data:`, { 
      id: gameData.id, 
      score: `${gameData.score.team1}-${gameData.score.team2}`,
      teams: `${gameData.teams.team1.id} vs ${gameData.teams.team2.id}`
    });
    
    const results: PayoutResult[] = [];
    const bets = this.getBetsForGame(gameId);
    
    console.log(`💰 [WALLET] Bets found for gameId ${gameId}:`, bets);
    console.log(`💰 [WALLET] Number of bets:`, Object.keys(bets).length);

    if (Object.keys(bets).length === 0) {
      console.log(`💰 [WALLET] No bets found for gameId ${gameId}`);
      return results;
    }

    // Process outcome bets
    const winningTeamId = gameData.score.team1 > gameData.score.team2
      ? gameData.teams.team1.id
      : gameData.score.team2 > gameData.score.team1
      ? gameData.teams.team2.id
      : null;

    console.log(`💰 [WALLET] Winning team ID: ${winningTeamId}`);
    console.log(`💰 [WALLET] Bet on winning team: ${winningTeamId ? bets[winningTeamId] : 'N/A'}`);
    console.log(`💰 [WALLET] Is bet paid out? ${winningTeamId ? this.isBetPaidOut(gameId, winningTeamId) : 'N/A'}`);

    if (winningTeamId && bets[winningTeamId] && !this.isBetPaidOut(gameId, winningTeamId)) {
      console.log(`💰 [WALLET] Processing winning outcome bet for team ${winningTeamId}`);
      const outcomeResult = this.calculatePayout(gameId, winningTeamId, 2.0);
      console.log(`💰 [WALLET] Outcome result:`, outcomeResult);
      results.push(outcomeResult);
    } else if (winningTeamId === null) {
      // Tie game - mark all outcome bets as paid out and refund
      const team1Bet = bets[gameData.teams.team1.id] || 0;
      const team2Bet = bets[gameData.teams.team2.id] || 0;
      if (team1Bet > 0 && !this.isBetPaidOut(gameId, gameData.teams.team1.id)) {
        this.addCredits(team1Bet);
        this.markBetAsPaidOut(gameId, gameData.teams.team1.id);
      }
      if (team2Bet > 0 && !this.isBetPaidOut(gameId, gameData.teams.team2.id)) {
        this.addCredits(team2Bet);
        this.markBetAsPaidOut(gameId, gameData.teams.team2.id);
      }
    }

    // Process team over/under bets
    const team1PtsOverKey = 'team1-pts-over';
    const team1PtsUnderKey = 'team1-pts-under';
    const team2PtsOverKey = 'team2-pts-over';
    const team2PtsUnderKey = 'team2-pts-under';

    if (bets[team1PtsOverKey] && !this.isBetPaidOut(gameId, team1PtsOverKey)) {
      const result = this.calculateOverUnderPayout(
        gameId,
        team1PtsOverKey,
        gameData.score.team1,
        team1AvgPoints,
        2.0
      );
      results.push(result);
    }

    if (bets[team1PtsUnderKey] && !this.isBetPaidOut(gameId, team1PtsUnderKey)) {
      const result = this.calculateOverUnderPayout(
        gameId,
        team1PtsUnderKey,
        gameData.score.team1,
        team1AvgPoints,
        2.0
      );
      results.push(result);
    }

    if (bets[team2PtsOverKey] && !this.isBetPaidOut(gameId, team2PtsOverKey)) {
      const result = this.calculateOverUnderPayout(
        gameId,
        team2PtsOverKey,
        gameData.score.team2,
        team2AvgPoints,
        2.0
      );
      results.push(result);
    }

    if (bets[team2PtsUnderKey] && !this.isBetPaidOut(gameId, team2PtsUnderKey)) {
      const result = this.calculateOverUnderPayout(
        gameId,
        team2PtsUnderKey,
        gameData.score.team2,
        team2AvgPoints,
        2.0
      );
      results.push(result);
    }

    // Process player over/under bets
    const processPlayerBet = (
      betKey: string,
      playerId: string,
      statType: 'pts' | 'ast',
      threshold: number
    ) => {
      if (!bets[betKey] || this.isBetPaidOut(gameId, betKey)) {
        return;
      }

      // Find player in game stats by ID
      // First try direct ID match
      let player: GamePlayerStats | null = null;
      for (const p of [...gameData.playerStats.team1, ...gameData.playerStats.team2]) {
        if (p.id === playerId) {
          player = p;
          break;
        }
      }

      // If not found and player stats don't have IDs, we need to match by name
      // This requires looking up the player name from the selected players
      // For now, if player not found, refund the bet
      if (!player) {
        const betAmount = bets[betKey];
        this.addCredits(betAmount);
        this.markBetAsPaidOut(gameId, betKey);
        results.push({
          won: false,
          payout: betAmount,
          newTotal: this.getCredits(),
          betKey,
          betType: 'over-under'
        });
        return;
      }

      const actualValue = statType === 'pts' ? player.points : player.assists;
      const result = this.calculateOverUnderPayout(gameId, betKey, actualValue, threshold, 2.0);
      results.push(result);
    };

    // Process team1 points player
    if (selectedPlayers.team1Points) {
      const playerId = selectedPlayers.team1Points.id;
      const overKey = `player-${playerId}-pts-over`;
      const underKey = `player-${playerId}-pts-under`;
      
      processPlayerBet(overKey, playerId, 'pts', selectedPlayers.team1Points.avgPoints);
      processPlayerBet(underKey, playerId, 'pts', selectedPlayers.team1Points.avgPoints);
    }

    // Process team1 assists player
    if (selectedPlayers.team1Assists) {
      const playerId = selectedPlayers.team1Assists.id;
      const overKey = `player-${playerId}-ast-over`;
      const underKey = `player-${playerId}-ast-under`;
      
      processPlayerBet(overKey, playerId, 'ast', selectedPlayers.team1Assists.avgAssists);
      processPlayerBet(underKey, playerId, 'ast', selectedPlayers.team1Assists.avgAssists);
    }

    // Process team2 points player
    if (selectedPlayers.team2Points) {
      const playerId = selectedPlayers.team2Points.id;
      const overKey = `player-${playerId}-pts-over`;
      const underKey = `player-${playerId}-pts-under`;
      
      processPlayerBet(overKey, playerId, 'pts', selectedPlayers.team2Points.avgPoints);
      processPlayerBet(underKey, playerId, 'pts', selectedPlayers.team2Points.avgPoints);
    }

    // Process team2 assists player
    if (selectedPlayers.team2Assists) {
      const playerId = selectedPlayers.team2Assists.id;
      const overKey = `player-${playerId}-ast-over`;
      const underKey = `player-${playerId}-ast-under`;
      
      processPlayerBet(overKey, playerId, 'ast', selectedPlayers.team2Assists.avgAssists);
      processPlayerBet(underKey, playerId, 'ast', selectedPlayers.team2Assists.avgAssists);
    }

    // Don't clear bets - keep them visible for indicators even after game completes
    // Bets are already marked as paid out, so they won't be processed again
    // this.clearBetsForGame(gameId);

    return results;
  }

  // Check if there are any bets that match team composition (fallback for ID migration)
  static findBetByTeams(team1Id: string, team2Id: string, slot: number): BetSearchResult | null {
    const possibleGameId = `next-${slot}-${team1Id}-${team2Id}`;
    const bets = this.getBetsForGame(possibleGameId);
    return Object.keys(bets).length > 0 ? { gameId: possibleGameId, bets } : null;
  }
} 